/**
 * TemplateLibrary — grid of saved landing templates (public + tenant).
 *
 * Each tile shows thumbnail SVG + name + tags + a trio of actions
 * (Apply / Rename / Delete). Public seed templates render Apply only —
 * they can't be renamed or deleted from the UI, only cloned.
 */

import { useState } from 'react'
import { Copy, FilePlus2, Pencil, Trash2 } from 'lucide-react'

export interface LandingTemplate {
  id: string
  tenant_id: string | null
  name: string
  description: string | null
  html_body: string
  tags: string[]
  thumbnail_svg: string | null
  is_public: boolean
  placeholders: { required?: string[]; optional?: string[] }
  slug?: string
}

interface TemplateLibraryProps {
  templates: LandingTemplate[]
  onApply: (tpl: LandingTemplate) => void
  onRename?: (tpl: LandingTemplate, newName: string) => void
  onDelete?: (tpl: LandingTemplate) => void
}

export function TemplateLibrary({ templates, onApply, onRename, onDelete }: TemplateLibraryProps) {
  const [renaming, setRenaming] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')

  if (templates.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-600">
        No templates yet. Save the current landing as a template to reuse it for another event.
      </div>
    )
  }

  return (
    <ul
      data-testid="template-library"
      className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
    >
      {templates.map((tpl) => {
        const tenantOwned = !tpl.is_public
        const isRenaming = renaming === tpl.id
        return (
          <li
            key={tpl.id}
            data-testid={`template-${tpl.slug || tpl.id}`}
            className="flex flex-col rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
          >
            <div
              className="flex h-24 items-center justify-center overflow-hidden rounded-md bg-slate-50 dark:bg-slate-900"
              dangerouslySetInnerHTML={{ __html: tpl.thumbnail_svg || defaultThumb() }}
            />
            <div className="mt-3 flex-1">
              {isRenaming ? (
                <input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={() => {
                    if (draftName.trim() && draftName.trim() !== tpl.name) {
                      onRename?.(tpl, draftName.trim())
                    }
                    setRenaming(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                    if (e.key === 'Escape') setRenaming(null)
                  }}
                  className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
                  aria-label="Rename template"
                />
              ) : (
                <h3 className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {tpl.name}
                  {tpl.is_public && (
                    <span className="ml-2 rounded bg-butler-500/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-butler-600">
                      Public
                    </span>
                  )}
                </h3>
              )}
              {tpl.description && (
                <p className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
                  {tpl.description}
                </p>
              )}
              {tpl.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {tpl.tags.slice(0, 4).map((t) => (
                    <span
                      key={t}
                      className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onApply(tpl)}
                className="flex-1 rounded-md bg-marquee-500 px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-marquee-600"
                aria-label={`Apply ${tpl.name}`}
              >
                {tenantOwned ? (
                  <span className="flex items-center justify-center gap-1"><FilePlus2 className="h-3 w-3" />Apply</span>
                ) : (
                  <span className="flex items-center justify-center gap-1"><Copy className="h-3 w-3" />Apply</span>
                )}
              </button>
              {tenantOwned && onRename && (
                <button
                  type="button"
                  onClick={() => {
                    setDraftName(tpl.name)
                    setRenaming(tpl.id)
                  }}
                  aria-label={`Rename ${tpl.name}`}
                  className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
              {tenantOwned && onDelete && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Delete template "${tpl.name}"?`)) onDelete(tpl)
                  }}
                  aria-label={`Delete ${tpl.name}`}
                  className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-rose-50 hover:text-rose-600 dark:border-slate-700 dark:text-slate-300"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function defaultThumb(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 60"><rect width="120" height="4" fill="#0070AB"/><rect x="12" y="18" width="96" height="8" fill="#44566C"/><rect x="12" y="32" width="60" height="4" fill="#8697A8"/></svg>`
}
